import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    CheckCircle,
    CreditCard,
    Store,
    User,
    DollarSign,
    Calendar,
    Image as ImageIcon,
    FileText,
    AlertCircle,
    Clock,
    XCircle
} from 'lucide-react';
import { useGetMarketplaceTransactionsQuery, useUpdateMarketplaceTransactionStatusMutation } from '../../store/slices/apiSlice';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import toast from 'react-hot-toast';

const MarketplaceTransactionDetails: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const transactionId = id ? parseInt(id) : null;
    const isValidId = transactionId && !isNaN(transactionId) && transactionId > 0;

    const { data: transactionsData, isLoading, error, refetch } = useGetMarketplaceTransactionsQuery({
        page: 1,
        limit: 1000
    });

    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [updateStatus, setUpdateStatus] = useState<'processing' | 'completed' | 'failed' | 'cancelled' | 'refunded'>('completed');
    const [adminNotes, setAdminNotes] = useState('');
    const [markListingSold, setMarkListingSold] = useState(false);
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImageUrl, setSelectedImageUrl] = useState<string>('');
    const [updateTransactionStatus, { isLoading: isUpdating }] = useUpdateMarketplaceTransactionStatusMutation();

    const transactions = transactionsData?.data?.transactions || [];
    const transaction = transactions.find(t => t.transaction_id === transactionId);

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'completed':
                return <Badge variant="success" className="flex items-center gap-1"><CheckCircle className="w-3 h-3" />Completed</Badge>;
            case 'processing':
                return <Badge variant="warning" className="flex items-center gap-1"><Clock className="w-3 h-3" />Processing</Badge>;
            case 'failed':
                return <Badge variant="danger" className="flex items-center gap-1"><XCircle className="w-3 h-3" />Failed</Badge>;
            case 'cancelled':
                return <Badge variant="default" className="flex items-center gap-1"><XCircle className="w-3 h-3" />Cancelled</Badge>;
            case 'refunded':
                return <Badge variant="default" className="flex items-center gap-1"><AlertCircle className="w-3 h-3" />Refunded</Badge>;
            default:
                return <Badge variant="default">{status}</Badge>;
        }
    };

    const formatCurrency = (amount: number, currencySymbol?: string | null) => {
        const symbol = currencySymbol || '₦';
        return `${symbol}${amount.toLocaleString('en-US')}`;
    };

    const formatDate = (dateString: string | Date | null | undefined) => formatDateTimeUtil(dateString);;

    const handleUpdateStatus = () => {
        if (transaction) {
            setUpdateStatus(transaction.status);
            setAdminNotes(transaction.admin_notes || '');
            setMarkListingSold(false);
            setShowUpdateModal(true);
        }
    };

    const handleSaveStatusUpdate = async () => {
        if (!transaction) return;

        try {
            await updateTransactionStatus({
                transaction_id: transaction.transaction_id,
                status: updateStatus,
                admin_notes: adminNotes.trim() || undefined,
                mark_listing_sold: markListingSold
            }).unwrap();
            toast.success('Transaction status updated successfully');
            setShowUpdateModal(false);
            setAdminNotes('');
            setMarkListingSold(false);
            refetch();
        } catch (error: any) {
            toast.error(error?.data?.message || 'Failed to update transaction status');
        }
    };

    const handleViewImage = (imageUrl: string) => {
        setSelectedImageUrl(imageUrl);
        setShowImageModal(true);
    };

    if (!isValidId) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <p className="text-red-800">Invalid transaction ID</p>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="p-6">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </div>
        );
    }

    if (error || !transaction) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <p className="text-red-800">Error loading transaction. Please try again.</p>
                    <button
                        onClick={() => navigate('/admin/marketplace-transactions')}
                        className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate('/admin/marketplace-transactions')}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Transaction Details</h1>
                                <p className="text-gray-600">Transaction ID: #{transaction.transaction_id}</p>
                            </div>
                        </div>
                        <Button
                            onClick={handleUpdateStatus}
                            variant="primary"
                        >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Update Status
                        </Button>
                    </div>
                </div>
            </div>

            <div className="px-6 py-6">
                <div className="max-w-4xl mx-auto space-y-6">
                    {/* Transaction Information */}
                    <div className="bg-white rounded-lg shadow">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                <CreditCard className="w-5 h-5 mr-2" />
                                Transaction Information
                            </h2>
                        </div>
                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="text-sm font-medium text-gray-700">Transaction ID</label>
                                <p className="mt-1 text-sm font-semibold text-gray-900">#{transaction.transaction_id}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700">Status</label>
                                <div className="mt-1">{getStatusBadge(transaction.status)}</div>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700">Amount</label>
                                <p className="mt-1 text-lg font-semibold text-gray-900">{formatCurrency(transaction.amount, transaction.user?.currency_symbol)}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700">Payment Method</label>
                                <p className="mt-1 text-sm text-gray-900 capitalize">{transaction.payment_method?.replace('_', ' ') || 'N/A'}</p>
                            </div>
                            {transaction.wallet_amount > 0 && (
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Wallet Amount</label>
                                    <p className="mt-1 text-sm text-gray-900">{formatCurrency(transaction.wallet_amount, transaction.user?.currency_symbol)}</p>
                                </div>
                            )}
                            {transaction.external_reference && (
                                <div>
                                    <label className="text-sm font-medium text-gray-700">External Reference</label>
                                    <p className="mt-1 text-sm text-gray-900">{transaction.external_reference}</p>
                                </div>
                            )}
                            <div>
                                <label className="text-sm font-medium text-gray-700">Created At</label>
                                <div className="mt-1 flex items-center gap-2">
                                    <Calendar className="w-4 h-4 text-gray-400" />
                                    <p className="text-sm text-gray-900">{formatDate(transaction.created_at)}</p>
                                </div>
                            </div>
                            {transaction.verified_at && (
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Verified At</label>
                                    <div className="mt-1 flex items-center gap-2">
                                        <Calendar className="w-4 h-4 text-gray-400" />
                                        <p className="text-sm text-gray-900">{formatDate(transaction.verified_at)}</p>
                                    </div>
                                </div>
                            )}
                            {transaction.is_large_transaction && (
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Transaction Type</label>
                                    <div className="mt-1">
                                        <Badge variant="warning">Large Transaction</Badge>
                                    </div>
                                </div>
                            )}
                            {transaction.is_below_min_threshold && (
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Threshold Status</label>
                                    <div className="mt-1">
                                        <Badge variant="danger">Below Minimum</Badge>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Listing Information */}
                    {transaction.listing && (
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                    <Store className="w-5 h-5 mr-2" />
                                    Listing Information
                                </h2>
                            </div>
                            <div className="p-6">
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Listing Title</label>
                                    <p className="mt-1 text-sm text-gray-900">{transaction.listing.title}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* User Information */}
                    {transaction.user && (
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                    <User className="w-5 h-5 mr-2" />
                                    User Information
                                </h2>
                            </div>
                            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Name</label>
                                    <p className="mt-1 text-sm text-gray-900">{transaction.user.name}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Email</label>
                                    <p className="mt-1 text-sm text-gray-900">{transaction.user.email}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Payment Screenshot */}
                    {transaction.payment_screenshot && (
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                    <ImageIcon className="w-5 h-5 mr-2" />
                                    Payment Screenshot
                                </h2>
                            </div>
                            <div className="p-6">
                                <div 
                                    className="relative group cursor-pointer rounded-lg overflow-hidden border-2 border-gray-200 hover:border-blue-500 transition-all bg-gray-100"
                                    onClick={() => handleViewImage(transaction.payment_screenshot!)}
                                >
                                    <div className="min-h-[300px] flex items-center justify-center">
                                        <img
                                            src={transaction.payment_screenshot}
                                            alt="Payment Screenshot"
                                            className="w-full h-full max-h-[500px] object-contain group-hover:scale-105 transition-transform duration-300"
                                        />
                                    </div>
                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all flex items-center justify-center pointer-events-none">
                                        <ImageIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </div>
                                </div>
                                <p className="mt-4 text-sm text-gray-500 text-center">Click on the image to view in full size</p>
                            </div>
                        </div>
                    )}

                    {/* Admin Notes */}
                    {transaction.admin_notes && (
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                    <FileText className="w-5 h-5 mr-2" />
                                    Admin Notes
                                </h2>
                            </div>
                            <div className="p-6">
                                <p className="text-sm text-gray-900 whitespace-pre-wrap">{transaction.admin_notes}</p>
                            </div>
                        </div>
                    )}

                    {/* Verification Information */}
                    {transaction.verified_by && (
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                    <CheckCircle className="w-5 h-5 mr-2" />
                                    Verification Information
                                </h2>
                            </div>
                            <div className="p-6">
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Verified By</label>
                                    <p className="mt-1 text-sm text-gray-900">{transaction.verified_by}</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Update Status Modal */}
            <Modal
                isOpen={showUpdateModal}
                onClose={() => {
                    setShowUpdateModal(false);
                    setAdminNotes('');
                    setMarkListingSold(false);
                }}
                title="Update Transaction Status"
            >
                <div className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                        <select
                            value={updateStatus}
                            onChange={(e) => setUpdateStatus(e.target.value as any)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="processing">Processing</option>
                            <option value="completed">Completed</option>
                            <option value="failed">Failed</option>
                            <option value="cancelled">Cancelled</option>
                            <option value="refunded">Refunded</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Admin Notes</label>
                        <textarea
                            value={adminNotes}
                            onChange={(e) => setAdminNotes(e.target.value)}
                            rows={4}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter admin notes..."
                        />
                    </div>
                    {updateStatus === 'completed' && transaction.listing && (
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="markSold"
                                checked={markListingSold}
                                onChange={(e) => setMarkListingSold(e.target.checked)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <label htmlFor="markSold" className="ml-2 text-sm font-medium text-gray-700">
                                Mark listing as sold
                            </label>
                        </div>
                    )}
                    <div className="flex justify-end gap-3 pt-4">
                        <Button
                            onClick={() => {
                                setShowUpdateModal(false);
                                setAdminNotes('');
                                setMarkListingSold(false);
                            }}
                            variant="outline"
                            disabled={isUpdating}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSaveStatusUpdate}
                            variant="primary"
                            disabled={isUpdating}
                            isLoading={isUpdating}
                        >
                            Update Status
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Image Modal */}
            <Modal
                isOpen={showImageModal}
                onClose={() => {
                    setShowImageModal(false);
                    setSelectedImageUrl('');
                }}
                title="Payment Screenshot"
            >
                <div className="p-6">
                    <img
                        src={selectedImageUrl}
                        alt="Payment Screenshot"
                        className="w-full h-auto rounded-lg"
                    />
                </div>
            </Modal>
        </div>
    );
};

export default MarketplaceTransactionDetails;

